import React, { useState, useEffect } from "react";
import { Sun, Moon } from "lucide-react";

interface HeaderProps {
  useRouterLinks?: boolean;
}

const Header: React.FC<HeaderProps> = ({ useRouterLinks = true }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState("/");
  const [theme, setTheme] = useState<"light" | "dark">("dark");

  // Initialize theme - default to dark
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark" | null;
    const initialTheme = savedTheme || "dark";
    setTheme(initialTheme);
    document.documentElement.setAttribute("data-theme", initialTheme);
    localStorage.setItem("theme", initialTheme);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
    localStorage.setItem("theme", newTheme);
  };

  useEffect(() => {
    if (useRouterLinks) {
      setCurrentPath(window.location.pathname);
    } else {
      setCurrentPath(window.location.pathname);
    }
  }, [useRouterLinks]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Events", path: "/events" },
    { name: "Schedule", path: "/clock" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
    { name: "Partners", path: "/sponsors" },
    { name: "Hackathon", path: "/hackathon" },
        { name: "Passes", path: "/passes" },

  ];

  const isActiveLink = (path: string) => {
    if (path === "/") {
      return currentPath === "/";
    }
    return currentPath.startsWith(path);
  };

  const handleNavigation = (path: string) => {
    if (!useRouterLinks) {
      if (path.startsWith("/#")) {
        window.location.href = "/";
        setTimeout(() => {
          const sectionId = path.split("#")[1];
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        window.location.href = path;
        setCurrentPath(path);
      }
    }
  };

  // Get theme colors dynamically
  const getThemeColors = () => {
    if (theme === "dark") {
      return {
        primary:
          getComputedStyle(document.documentElement).getPropertyValue(
            "--color-primary"
          ) || "#dc2626",
        primaryLight:
          getComputedStyle(document.documentElement).getPropertyValue(
            "--color-primary-light"
          ) || "#ef4444",
        border:
          getComputedStyle(document.documentElement).getPropertyValue(
            "--color-border"
          ) || "#262626",
        surface:
          getComputedStyle(document.documentElement).getPropertyValue(
            "--color-surface"
          ) || "#171717",
      };
    }
    return {
      primary:
        getComputedStyle(document.documentElement).getPropertyValue(
          "--color-primary"
        ) || "#3b82f6",
      primaryLight:
        getComputedStyle(document.documentElement).getPropertyValue(
          "--color-primary-light"
        ) || "#60a5fa",
      border:
        getComputedStyle(document.documentElement).getPropertyValue(
          "--color-border"
        ) || "#dbeafe",
      surface: "#ffffff",
    };
  };

  // Styles based on theme
  const headerStyles = {
    backgroundColor: isScrolled
      ? theme === "dark"
        ? "rgba(0, 0, 0, 0.8)"
        : "rgba(255, 255, 255, 0.8)"
      : "transparent",
    backdropFilter: isScrolled ? "blur(16px)" : "none",
    borderBottom: isScrolled
      ? `2px solid ${
          theme === "dark"
            ? "rgba(220, 38, 38, 0.3)"
            : "rgba(59, 130, 246, 0.3)"
        }`
      : "none",
    boxShadow: isScrolled ? "0 4px 6px -1px rgba(0, 0, 0, 0.1)" : "none",
  };

  const renderNavLink = (link: any, index: number) => {
    const isActive = isActiveLink(link.path);
    const colors = getThemeColors();

    const linkContent = (
      <>
        <span
          className={`relative z-10 transition-colors duration-300 ${
            isActive ? "text-white" : ""
          }`}
        >
          {link.name}
        </span>
        <div
          className={`absolute inset-0 rounded-lg transition-all duration-300 ${
            isActive
              ? "opacity-100 transform scale-100"
              : "opacity-0 group-hover:opacity-100 transform scale-95 group-hover:scale-100"
          }`}
          style={{
            background: isActive
              ? // Always use red gradient for home, theme colors for others
                link.path === "/"
                ? "linear-gradient(to right, #dc2626, #ef4444)"
                : `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})`
              : `linear-gradient(to right, ${colors.primaryLight}20, ${colors.primary}20)`,
          }}
        />
      </>
    );

    const className = `relative group px-4 py-2 font-medium text-sm transition-all duration-300 ${
      isActive
        ? ""
        : "text-gray-700 dark:text-gray-300 hover:text-[var(--color-primary)]"
    }`;

    if (useRouterLinks) {
      return (
        <a
          key={link.name}
          href={link.path}
          className={className}
          style={{ animationDelay: `${index * 0.1}s` }}
        >
          {linkContent}
        </a>
      );
    } else {
      return (
        <div
          key={link.name}
          onClick={() => handleNavigation(link.path)}
          className={`${className} cursor-pointer`}
          style={{ animationDelay: `${index * 0.1}s` }}
        >
          {linkContent}
        </div>
      );
    }
  };

  return (
    <>
     <header
  className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
  style={headerStyles}
>
  <nav className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="flex items-center justify-between h-16 md:h-20">
      {/* Logo - Left side with adjusted spacing */}
      <div className="flex-shrink-0">
        <div className="flex items-center gap-3 group cursor-pointer">
          {useRouterLinks ? (
            <a href="/" className="flex items-center gap-3">
              {renderLogoContent()}
            </a>
          ) : (
            <div
              onClick={() => handleNavigation("/")}
              className="flex items-center gap-3"
            >
              {renderLogoContent()}
            </div>
          )}
        </div>
      </div>

      {/* Desktop Navigation - Center */}
      <div className="hidden lg:flex items-center space-x-1 flex-1 justify-center">
        {navLinks.map((link, index) => renderNavLink(link, index))}
      </div>

      {/* Desktop Actions - Right side */}
      <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
        {/* Theme Toggle Button */}
      </div>

      {/* Mobile Actions */}
      <div className="lg:hidden flex items-center gap-2 flex-shrink-0">
        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="relative w-10 h-10 rounded-lg backdrop-blur-sm flex items-center justify-center transition-all duration-300 hover:scale-105"
          style={{
            backgroundColor:
              theme === "dark"
                ? "rgba(23, 23, 23, 0.7)"
                : "rgba(255, 255, 255, 0.7)",
            border: `2px solid ${
              theme === "dark"
                ? "rgba(220, 38, 38, 0.5)"
                : "rgba(59, 130, 246, 0.5)"
            }`,
          }}
          aria-label="Toggle menu"
        >
          <div className="w-5 h-4 flex flex-col justify-between">
            <span
              className={`w-full h-0.5 rounded-full transform transition-all duration-300 ${
                isMobileMenuOpen ? "rotate-45 translate-y-1.5" : ""
              }`}
              style={{ backgroundColor: "var(--color-primary)" }}
            />
            <span
              className={`w-full h-0.5 rounded-full transition-all duration-300 ${
                isMobileMenuOpen ? "opacity-0" : ""
              }`}
              style={{ backgroundColor: "var(--color-primary)" }}
            />
            <span
              className={`w-full h-0.5 rounded-full transform transition-all duration-300 ${
                isMobileMenuOpen ? "-rotate-45 -translate-y-1.5" : ""
              }`}
              style={{ backgroundColor: "var(--color-primary)" }}
            />
          </div>
        </button>
      </div>
    </div>
  </nav>
</header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isMobileMenuOpen ? "visible" : "invisible"
        }`}
      >
        <div
          className={`absolute inset-0 backdrop-blur-sm transition-opacity duration-500 ${
            isMobileMenuOpen ? "opacity-100" : "opacity-0"
          }`}
          style={{
            backgroundColor:
              theme === "dark" ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.5)",
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        <div
          className={`absolute top-0 right-0 w-full sm:w-80 h-full transform transition-transform duration-500 ${
            isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
          style={{
            background:
              theme === "dark"
                ? "linear-gradient(to bottom right, #0f172a, #1e293b)"
                : "linear-gradient(to bottom right, #ffffff, #f8fafc)",
          }}
        >
          {/* Menu Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="relative transform transition-transform duration-300">
                {/* Add your logo or icon here if needed */}
              </div>
              <div>
                <h2
                  className="text-lg font-black tracking-tight"
                  style={{
                    color: theme === "dark" ? "#ffffff" : "#1f2937",
                  }}
                >
                  
                </h2>
              </div>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
              aria-label="Close menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Menu Links */}
          <nav className="p-6 space-y-2">
            {navLinks.map((link, index) => {
              const isActive = isActiveLink(link.path);
              const colors = getThemeColors();
              const linkElement = useRouterLinks ? (
                <a
                  key={link.name}
                  href={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                    isActive ? "border-2 shadow-lg" : "border hover:shadow-lg"
                  } ${
                    isMobileMenuOpen
                      ? "translate-x-0 opacity-100"
                      : "translate-x-4 opacity-0"
                  }`}
                  style={{
                    transitionDelay: isMobileMenuOpen
                      ? `${index * 0.05 + 0.3}s`
                      : "0s",
                    background: isActive
                      ? `linear-gradient(to right, ${colors.primary}, ${colors.primary})`
                      : theme === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                    borderColor: isActive
                      ? colors.primary
                      : theme === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: isActive ? "white" : colors.primary,
                    }}
                  />
                  <span
                    className={`font-semibold transition-colors duration-300 ${
                      isActive
                        ? "text-white"
                        : theme === "dark"
                        ? "text-gray-200 hover:text-[var(--color-primary)]"
                        : "text-gray-800 hover:text-[var(--color-primary)]"
                    }`}
                  >
                    {link.name}
                  </span>
                  <svg
                    className="w-5 h-5 ml-auto transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{
                      color: isActive
                        ? "white"
                        : theme === "dark"
                        ? "#9ca3af"
                        : "#6b7280",
                    }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </a>
              ) : (
                <div
                  key={link.name}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleNavigation(link.path);
                  }}
                  className={`group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 cursor-pointer ${
                    isActive ? "border-2 shadow-lg" : "border hover:shadow-lg"
                  } ${
                    isMobileMenuOpen
                      ? "translate-x-0 opacity-100"
                      : "translate-x-4 opacity-0"
                  }`}
                  style={{
                    transitionDelay: isMobileMenuOpen
                      ? `${index * 0.05 + 0.3}s`
                      : "0s",
                    background: isActive
                      ? `linear-gradient(to right, ${colors.primary}, white)`
                      : theme === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                    borderColor: isActive
                      ? colors.primary
                      : theme === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: isActive ? "white" : colors.primary,
                    }}
                  />
                  <span
                    className={`font-semibold transition-colors duration-300 ${
                      isActive
                        ? "text-white"
                        : theme === "dark"
                        ? "text-gray-200 hover:text-[var(--color-primary)]"
                        : "text-gray-800 hover:text-[var(--color-primary)]"
                    }`}
                  >
                    {link.name}
                  </span>
                  <svg
                    className="w-5 h-5 ml-auto transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{
                      color: isActive
                        ? "white"
                        : theme === "dark"
                        ? "#9ca3af"
                        : "#6b7280",
                    }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              );
              return linkElement;
            })}
          </nav>

          {/* Mobile CTA */}
          <div className="p-6 pt-0">
            {useRouterLinks ? (
              <a
                href="/registration"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <button className="w-full group relative px-6 py-4 rounded-xl font-bold text-white overflow-hidden transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <span>Register Now</span>
                    <svg
                      className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </span>
                  <div
                    className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300"
                    style={{
                      background:
                        "linear-gradient(to right, var(--color-primary), var(--color-accent))",
                    }}
                  />
                </button>
              </a>
            ) : (
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleNavigation("/registration");
                }}
                className="w-full group relative px-6 py-4 rounded-xl font-bold text-white overflow-hidden transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <span>Register Now</span>
                  <svg
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </span>
                <div
                  className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300"
                  style={{
                    background:
                      "linear-gradient(to right, var(--color-primary), var(--color-accent))",
                  }}
                />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const renderLogoContent = () => (
  <div className="flex items-center gap-3 group">
    <div className="relative transform group-hover:scale-110 transition-transform duration-300">
      <img
        src="https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/website/ASTRANOVA.png"
        alt="ASTRANOVA Logo"
        className="w-12 h-12 md:w-14 md:h-14 object-contain"
      />
    </div>
    <div>
      <h1
        className="text-xl md:text-2xl font-black tracking-tight text-transparent"
        style={{
          background:
            "linear-gradient(to right, var(--color-primary-dark), var(--color-secondary))",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
        }}
      >
        ASTRANOVA
      </h1>
      <p
        className="text-xs font-medium hidden sm:block"
        style={{ color: "var(--color-text-secondary)" }}
      >
        Tech Symposium 2026
      </p>
    </div>
  </div>
);

export default Header;
